import React, { useState, useRef, useEffect, useMemo } from "react";
import AmountRangePopover from "./AmountRangePopover";
import CurrencyPopover from "./CurrencyPopover";
import StatusPopover from "./StatusPopover";
import TypePopover from "./TypePopover";
import DatePopover from "./DatePopover";
// EditColumnsPopover removed — columns are now fixed per tab
import WithdrawRequestsPage from "./Pages/WithdrawRequestsPage";
import NewPayoutRequest from "./Pages/NewPayoutRequest";
import { API_BASE_URL, QR_BASE_URL } from "../utils/apiConfig";
import usePermissions from "../hooks/usePermissions";
import Permission from "./Permission";
import { FaCalendarAlt } from "react-icons/fa";
import { GoClockFill } from "react-icons/go";
import { LuSquareArrowOutUpRight } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import {
  QrCode,
  Eye,
  X,
  Copy,
  ExternalLink,
  Calendar,
  DollarSign,
  Hash,
  Wallet,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Plus,
  Link as LinkIcon,
  Loader2,
  ChevronDown,
  Download,
  FileSpreadsheet,
  CheckSquare,
  Search,
} from "lucide-react";
import * as XLSX from "xlsx";

const NewTransaction = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState("All");

  // Create Payment Link modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ amount: '', currencyType: 'USDT-TRC20', timer: '', description: '' });
  const [createdPaymentLink, setCreatedPaymentLink] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [amountPopoverOpen, setAmountPopoverOpen] = useState(false);
  const [amountFilter, setAmountFilter] = useState({ min: 0, max: 100000 });
  const [currencyPopoverOpen, setCurrencyPopoverOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [typePopoverOpen, setTypePopoverOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  // editColumnsPopoverOpen removed — column customization removed
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState({
    id: "all",
    name: "All Currencies",
    symbol: "ALL",
  });
  const [selectedStatus, setSelectedStatus] = useState({
    id: "",
    name: "",
    color: "green",
  });
  const [selectedType, setSelectedType] = useState({ id: "", name: "" });

  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [timer, setTimer] = useState("");
  const [userId, setUserId] = useState("");
  const [apiCredentialsLoading, setApiCredentialsLoading] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);

  // Date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to 7 days ago
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Generate date ranges based on preset selection
  const generateDateRange = (preset) => {
    const today = new Date();
    const formatDate = (date) => {
      return date.toISOString().split("T")[0];
    };

    switch (preset) {
      case "today":
        return {
          fromDate: formatDate(today),
          toDate: formatDate(today),
        };

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return {
          fromDate: formatDate(yesterday),
          toDate: formatDate(yesterday),
        };

      case "tomorrow":
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return {
          fromDate: formatDate(tomorrow),
          toDate: formatDate(tomorrow),
        };

      case "thisWeek":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          fromDate: formatDate(startOfWeek),
          toDate: formatDate(endOfWeek),
        };

      case "lastWeek":
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return {
          fromDate: formatDate(lastWeekStart),
          toDate: formatDate(lastWeekEnd),
        };

      case "thisMonth":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
        );
        return {
          fromDate: formatDate(startOfMonth),
          toDate: formatDate(endOfMonth),
        };

      case "lastMonth":
        const lastMonthStart = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          fromDate: formatDate(lastMonthStart),
          toDate: formatDate(lastMonthEnd),
        };

      case "thisYear":
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        return {
          fromDate: formatDate(startOfYear),
          toDate: formatDate(endOfYear),
        };

      case "lastYear":
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
        return {
          fromDate: formatDate(lastYearStart),
          toDate: formatDate(lastYearEnd),
        };

      case "last7Days":
        const last7Days = new Date(today);
        last7Days.setDate(today.getDate() - 7);
        return {
          fromDate: formatDate(last7Days),
          toDate: formatDate(today),
        };

      case "last30Days":
        const last30Days = new Date(today);
        last30Days.setDate(today.getDate() - 30);
        return {
          fromDate: formatDate(last30Days),
          toDate: formatDate(today),
        };

      case "last90Days":
        const last90Days = new Date(today);
        last90Days.setDate(today.getDate() - 90);
        return {
          fromDate: formatDate(last90Days),
          toDate: formatDate(today),
        };

      default:
        return {
          fromDate: "",
          toDate: "",
        };
    }
  };

  // Initialize date filter with no dates by default
  const getInitialDateFilter = () => {
    return {
      fromDate: "",
      toDate: "",
      preset: "",
    };
  };

  const [dateFilter, setDateFilter] = useState(getInitialDateFilter);
  // Columns are fixed per tab — no customization needed
  const visibleColumns = useMemo(() => {
    const base = { checkbox: true, amount: true, currency: true, status: true, receivingWallet: true, date: true };
    if (selectedTab === 'All') return { ...base, type: true };
    return base;
  }, [selectedTab]);
  const amountButtonRef = useRef(null);
  const currencyButtonRef = useRef(null);
  const statusButtonRef = useRef(null);
  const typeButtonRef = useRef(null);
  const dateButtonRef = useRef(null);
  // editColumnsButtonRef removed — column customization removed

  // Get user data from localStorage
  const getUserDataFromStorage = () => {
    try {
      const storedUserId = localStorage.getItem("userId");
      const storedUserRole = localStorage.getItem("userRole");
      const storedUserEmail = localStorage.getItem("userEmail");
      const storedExpiry = localStorage.getItem("token_expiry");

      if (!storedUserId) {
        console.error("No userId found in localStorage");
        return null;
      }

      // Check if session has expired
      if (storedExpiry && Date.now() > parseInt(storedExpiry)) {
        console.error("Session expired");
        localStorage.clear();
        window.location.href = "#/";
        return null;
      }

      setUserId(storedUserId);
      return storedUserId;
    } catch (error) {
      console.error("Failed to get user data from localStorage:", error);
      return null;
    }
  };

  // Fetch API credentials
  const fetchApiKey = async () => {
    const currentUserId = getUserDataFromStorage();

    if (!currentUserId) {
      return;
    }

    try {
      setApiCredentialsLoading(true);
      // Use the dedicated API credentials endpoint for secure access
      const response = await fetch(
        `${API_BASE_URL}/merchant/${currentUserId}/api-credentials`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch API key: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Extract API credentials from the dedicated endpoint response
      let foundApiKey = null;
      let foundApiSecret = null;

      // The dedicated endpoint returns data in data.data structure
      if (data && data.data) {
        foundApiKey = data.data.apiKey || null;
        foundApiSecret = data.data.secretIsHashed ? null : (data.data.apiSecret || null);
      }

      let hasErrors = false;
      let errorMessages = [];

      if (foundApiKey) {
        setApiKey(foundApiKey);
      } else {
        hasErrors = true;
        errorMessages.push("API Key not found in response");
      }

      if (foundApiSecret) {
        setApiSecret(foundApiSecret);
      } else {
        hasErrors = true;
        errorMessages.push("API Secret not found in response");
      }

      if (data && data.data && data.data.transactionTimer) {
        setTimer(data.data.transactionTimer);
      }

      if (hasErrors) {
        console.error(
          errorMessages.join(". ") + ". Check the API response structure.",
        );
      }
    } catch (error) {
      console.error("Error fetching API credentials:", error);
    } finally {
      setApiCredentialsLoading(false);
    }
  };

  // Generate QR Generator URL
  const generateQRGeneratorUrl = () => {
    const apiKeyEncoded = apiKey ? btoa(apiKey) : "";
    const apiSecretEncoded = apiSecret ? btoa(apiSecret) : "";
    return `${QR_BASE_URL}/qr-generator?api_key=${apiKeyEncoded}&api_secret=${apiSecretEncoded}&timer=${timer}`;
  };

  // Open QR Generator in new tab
  const openQRGenerator = () => {
    if (apiKey && apiSecret) {
      const url = generateQRGeneratorUrl();
      window.open(url, "_blank");
    } else if (apiKey && !apiSecret) {
      alert("Your API credentials have been secured. Please regenerate your credentials in Settings → API Credentials to use the QR generator.");
    } else {
      alert("API credentials not available. Please try refreshing the page.");
    }
  };

  // Open transaction details modal
  const openTransactionModal = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  // Close transaction details modal
  const closeTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // You could add a toast notification here
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Copy with visual feedback for modal
  const copyWithFeedback = (text, field) => {
    copyToClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Create Payment Link handlers
  const handleCreatePaymentLink = async () => {
    setCreateLoading(true);
    setCreateError('');
    try {
      const params = new URLSearchParams();
      params.append('currencyType', createForm.currencyType);
      if (createForm.amount) params.append('amount', createForm.amount);
      if (createForm.timer) params.append('timer', createForm.timer);
      if (createForm.description) params.append('description', createForm.description);
      const response = await fetch(`${API_BASE_URL}/transaction/create-payment-link?${params.toString()}`, { method: 'POST', credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setCreatedPaymentLink(data);
      } else {
        setCreateError(data.error || data.message || t('paymentLinks.failedToCreate'));
      }
    } catch (err) {
      setCreateError(err.message || t('paymentLinks.networkError'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatedPaymentLink(null);
    setCreateForm({ amount: '', currencyType: 'USDT-TRC20', timer: '', description: '' });
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    if (createError) setCreateError('');
  };

  // Format date to DD/MM/YYYY HH:MM:SS
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "--";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "--";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "--";
    }
  };

  // Format time to HH:MM:SS
  const formatTime = (dateString) => {
    if (!dateString) return "--";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "--";
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "--";
    }
  };

  // API function to fetch transactions
  const fetchTransactions = async (tabType = "all", page = 1, limit = 10) => {
    setLoading(true);
    setError(null);

    try {
      // If there's a search term, fetch ALL transactions for client-side search
      const isSearching = debouncedSearch && debouncedSearch.trim() !== "";
      const actualLimit = isSearching ? 10000 : limit; // Large limit when searching
      const actualPage = isSearching ? 1 : page; // Always page 1 when searching

      const params = new URLSearchParams({
        page: actualPage.toString(),
        limit: actualLimit.toString(),
        sort: "newest",
        filter: "range",
      });

      // Determine effective type: use type filter if active (All tab), otherwise use tab type
      // Only append once to avoid duplicate params
      const effectiveType = (selectedType?.id && selectedType.id !== "")
        ? selectedType.id.toLowerCase()
        : tabType.toLowerCase();
      params.append("type", effectiveType);

      // Add filters if they exist (but not search - we'll do client-side)
      if (amountFilter.min > 0) {
        params.append("minAmount", amountFilter.min.toString());
      }
      if (amountFilter.max < 100000) {
        params.append("maxAmount", amountFilter.max.toString());
      }
      if (selectedCurrency.id !== "all") {
        params.append("currency", selectedCurrency.id);
      }
      if (selectedStatus?.id && selectedStatus.id.toLowerCase() !== "all") {
        params.append("status", selectedStatus.id.toLowerCase());
      }
      // Only add date parameters if they have actual values
      if (dateFilter.fromDate && dateFilter.fromDate.trim() !== "") {
        params.append("startDate", dateFilter.fromDate);
      }
      if (dateFilter.toDate && dateFilter.toDate.trim() !== "") {
        params.append("endDate", dateFilter.toDate);
      }

      const response = await fetch(
        `${API_BASE_URL}/dashboard/transactionList?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        let transactionData = data.data.docs || [];

        if (isSearching) {
          // Apply client-side search when searching
          const searchLower = debouncedSearch.toLowerCase().trim();
          transactionData = transactionData.filter((transaction) => {
            const searchableFields = [
              transaction.transactionId,
              transaction.id,
              transaction.email,
              transaction.username,
              transaction.firstName,
              transaction.lastName,
              transaction.phoneNumber,
              transaction.referenceId,
              transaction.description,
              transaction.walletAddress,
              transaction.clientWallet,
              transaction.marchantUserId,
              transaction.invoiceNo,
              transaction.adminNotes,
              transaction.currencyType,
              transaction.amount?.toString(),
              transaction.netAmount?.toString(),
              transaction.fee?.toString(),
              transaction.status,
              transaction.type,
              transaction.blockchainTxHash,
              transaction.transactionHash,
            ].filter(Boolean); // Remove null/undefined values

            return searchableFields.some((field) =>
              field.toString().toLowerCase().includes(searchLower),
            );
          });

          // Apply client-side pagination to search results
          const totalResults = transactionData.length;
          const totalPages = Math.ceil(totalResults / limit);
          const startIndex = (page - 1) * limit;
          const endIndex = startIndex + limit;
          const paginatedResults = transactionData.slice(startIndex, endIndex);

          setTransactions(paginatedResults);
          setPagination({
            page: page,
            limit: limit,
            total: totalResults,
            totalPages: totalPages,
          });
        } else {
          // Normal pagination for non-search requests
          setTransactions(transactionData);
          setPagination({
            page: data.data.page || 1,
            limit: data.data.limit || 10,
            total: data.data.total || 0,
            totalPages: data.data.totalPages || 0,
          });
        }
      } else {
        throw new Error(data.error || "Failed to fetch transactions");
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError(err.message);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // API function to fetch ALL transactions for export (without pagination)
  const fetchAllTransactionsForExport = async (tabType = "all") => {
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "10000", // Large limit to get all records
        sort: "newest",
        filter: "range",
      });

      // Determine effective type: use type filter if active (All tab), otherwise use tab type
      // Only append once to avoid duplicate params
      const effectiveType = (selectedType?.id && selectedType.id !== "")
        ? selectedType.id.toLowerCase()
        : tabType.toLowerCase();
      params.append("type", effectiveType);

      // Add filters if they exist
      if (amountFilter.min > 0) {
        params.append("minAmount", amountFilter.min.toString());
      }
      if (amountFilter.max < 100000) {
        params.append("maxAmount", amountFilter.max.toString());
      }
      if (selectedCurrency.id !== "all") {
        params.append("currency", selectedCurrency.id);
      }
      if (selectedStatus?.id && selectedStatus.id.toLowerCase() !== "all") {
        params.append("status", selectedStatus.id.toLowerCase());
      }
      // Only add date parameters if they have actual values
      if (dateFilter.fromDate && dateFilter.fromDate.trim() !== "") {
        params.append("startDate", dateFilter.fromDate);
      }
      if (dateFilter.toDate && dateFilter.toDate.trim() !== "") {
        params.append("endDate", dateFilter.toDate);
      }

      // Add search parameter if it exists
      if (debouncedSearch && debouncedSearch.trim() !== "") {
        params.append("search", debouncedSearch.trim());
      }

      const response = await fetch(
        `${API_BASE_URL}/dashboard/transactionList?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        let allTransactions = data.data.docs || [];

        // Apply client-side search if there's a search term
        if (debouncedSearch && debouncedSearch.trim() !== "") {
          const searchLower = debouncedSearch.toLowerCase().trim();
          allTransactions = allTransactions.filter((transaction) => {
            const searchableFields = [
              transaction.transactionId,
              transaction.id,
              transaction.email,
              transaction.username,
              transaction.firstName,
              transaction.lastName,
              transaction.phoneNumber,
              transaction.referenceId,
              transaction.description,
              transaction.walletAddress,
              transaction.clientWallet,
              transaction.marchantUserId,
              transaction.invoiceNo,
              transaction.adminNotes,
              transaction.currencyType,
              transaction.amount?.toString(),
              transaction.netAmount?.toString(),
              transaction.fee?.toString(),
              transaction.status,
              transaction.type,
              transaction.blockchainTxHash,
              transaction.transactionHash,
            ].filter(Boolean); // Remove null/undefined values

            return searchableFields.some((field) =>
              field.toString().toLowerCase().includes(searchLower),
            );
          });
        }

        return allTransactions;
      } else {
        throw new Error(data.error || "Failed to fetch transactions");
      }
    } catch (err) {
      console.error("Error fetching all transactions for export:", err);
      throw err;
    }
  };

  // Fetch transactions when component mounts or tab changes
  useEffect(() => {
    const tabTypeMap = {
      All: "all",
      Deposit: "deposit",
      Payout: "payout",
      Withdrawal: "withdrawal",
    };

    const apiType = tabTypeMap[selectedTab] || "all";
    fetchTransactions(apiType);
  }, [selectedTab]);

  // Fetch transactions when filters change
  useEffect(() => {
    const tabTypeMap = {
      All: "all",
      Deposit: "deposit",
      Payout: "payout",
      Withdrawal: "withdrawal",
    };

    const apiType = tabTypeMap[selectedTab] || "all";
    fetchTransactions(apiType, pagination.page, pagination.limit);
  }, [
    amountFilter,
    selectedCurrency,
    selectedStatus,
    selectedType,
    dateFilter,
    debouncedSearch,
  ]);

  // Debounce search — waits 400ms after last keystroke before triggering fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest(".date-picker")) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Tab configuration with display names and API values
  const tabs = [
    { label: t("transactions.all"), value: "All" },
    { label: t("transactions.deposits"), value: "Deposit" },
    { label: t("transactions.payouts"), value: "Payout" },
    { label: t("transactions.withdrawal"), value: "Withdrawal" },
  ];

  // Helper function to format transaction data
  const formatTransaction = (transaction) => {
    const rawAmount = parseFloat(transaction.amount || 0);
    const actualBalance = parseFloat(transaction.actualBalance || 0);
    return {
      id: transaction._id || transaction.id,
      amount: `${transaction.amount || 0}`,
      rawAmount: rawAmount,
      currency: transaction.currencyType || "USDT TRC20",
      status: transaction.status || "Pending",
      type: transaction.invoiceNo || "N/A",
      invoiceNo: transaction.invoiceNo || "N/A",
      receivingWallet: transaction.walletAddress || "N/A",
      sendingWallet: transaction.clientId || "N/A",
      date: new Date(
        transaction.createdAt || transaction.confirmedAt,
      ).toLocaleString(),
      netAmount: transaction.netAmount || 0,
      fee: transaction.fee || 0,
      actualBalance: actualBalance,
      userDetails: transaction.userDetails || {},
      transactionType: transaction.transactionType || "",
      _transactionType: transaction._transactionType || null,
      hasActualBalance:
        transaction.actualBalance !== undefined &&
        transaction.actualBalance !== null,
      transactionId: transaction.transactionId,
      description: transaction.description,
      confirmedAt: transaction.confirmedAt,
      createdAt: transaction.createdAt,
      transactionHash: transaction.transactionHash,
      blockchainTxHash: transaction.blockchainTxHash,
      adminTransferData: transaction.adminTransferData,
      clientWallet: transaction.clientWallet,
      // User detail fields
      username: transaction.username,
      firstName: transaction.firstName,
      lastName: transaction.lastName,
      email: transaction.email,
      phoneNumber: transaction.phoneNumber,
      referenceId: transaction.referenceId,
      publicToken: transaction.publicToken,
    };
  };

  // Format transactions for display
  const formattedTransactions = useMemo(() => {
    return transactions.map(formatTransaction);
  }, [transactions]);

  const handleSelectAll = () => {
    if (selectedRows.size === formattedTransactions.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(formattedTransactions.map((t) => t.id)));
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleAmountFilterClick = () => {
    if (amountButtonRef.current) {
      const rect = amountButtonRef.current.getBoundingClientRect();
      setAmountPopoverOpen(true);
    }
  };

  const handleAmountFilterApply = (filter) => {
    setAmountFilter(filter);
    // Here you would typically filter the transactions based on the amount range
  };

  const handleCurrencyFilterClick = () => {
    if (currencyButtonRef.current) {
      const rect = currencyButtonRef.current.getBoundingClientRect();
      setCurrencyPopoverOpen(true);
    }
  };

  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency);
  };

  const handleStatusFilterClick = () => {
    if (statusButtonRef.current) {
      const rect = statusButtonRef.current.getBoundingClientRect();
      setStatusPopoverOpen(true);
    }
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    // Here you would typically filter the transactions based on the selected status
  };

  const handleTypeFilterClick = () => {
    if (typeButtonRef.current) {
      const rect = typeButtonRef.current.getBoundingClientRect();
      setTypePopoverOpen(true);
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleDateFilterClick = () => {
    if (dateButtonRef.current) {
      const rect = dateButtonRef.current.getBoundingClientRect();
      setDatePopoverOpen(true);
    }
  };

  const handleDateFilterApply = (filter) => {
    // If preset is selected, generate date range automatically
    if (filter.preset && filter.preset !== "custom") {
      const dateRange = generateDateRange(filter.preset);
      const updatedFilter = {
        ...filter,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      };
      setDateFilter(updatedFilter);
    } else {
      setDateFilter(filter);
    }
  };

  // handleEditColumnsClick and handleEditColumnsApply removed — column customization removed

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      amountFilter.min > 0 ||
      amountFilter.max < 100000 ||
      selectedCurrency.id !== "all" ||
      (selectedStatus.id && selectedStatus.id !== "") ||
      (selectedType.id && selectedType.id !== "") ||
      dateFilter.fromDate !== "" ||
      dateFilter.toDate !== "" ||
      searchTerm.trim() !== ""
    );
  };

  // Reset all filters
  const handleResetFilters = () => {
    setAmountFilter({ min: 0, max: 100000 });
    setSelectedCurrency({
      id: "all",
      name: t("transactions.allCurrencies"),
      symbol: "ALL",
    });
    setSelectedStatus({ id: "", name: "", color: "green" });
    setSelectedType({ id: "", name: "" });

    // Reset date filter to no dates
    setDateFilter({
      fromDate: "",
      toDate: "",
      preset: "",
    });

    // Reset search term
    setSearchTerm("");

    // Reset date picker to default values (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    setFromDate(sevenDaysAgo.toISOString().split("T")[0]);
    setToDate(new Date().toISOString().split("T")[0]);

    // Refetch data with reset filters
    const tabTypeMap = {
      All: "all",
      Deposit: "deposit",
      Payout: "payout",
      Withdrawal: "withdrawal",
    };
    const apiType = tabTypeMap[selectedTab] || "all";
    fetchTransactions(apiType, 1, pagination.limit);
  };

  const created = formatDateTime(selectedTransaction?.createdAt);
  const updated = formatDateTime(selectedTransaction?.confirmedAt);
  // handleResetColumns removed — column customization removed

  // Export transactions to Excel — mode: 'all' | 'selected' | 'page'
  const handleExport = async (mode = 'all') => {
    try {
      setExportLoading(true);
      setShowExportDropdown(false);

      let transactionsToExport;

      if (mode === 'page') {
        // Export only the current page — already loaded in state
        transactionsToExport = formattedTransactions;
      } else if (mode === 'selected') {
        // Export only checked rows from current page
        transactionsToExport = formattedTransactions.filter((t) => selectedRows.has(t.id));
      } else {
        // Export all transactions matching filters (fetch from API)
        const tabTypeMap = {
          All: "all",
          Deposit: "deposit",
          Payout: "payout",
          Withdrawal: "withdrawal",
        };
        const apiType = tabTypeMap[selectedTab] || "all";
        const allTransactions = await fetchAllTransactionsForExport(apiType);

        if (!allTransactions || allTransactions.length === 0) {
          alert(t("transactions.noTransactionsAvailable"));
          return;
        }
        transactionsToExport = allTransactions.map(formatTransaction);
      }

      if (!transactionsToExport || transactionsToExport.length === 0) {
        alert(t("transactions.noTransactionsAvailable"));
        return;
      }

      // Prepare data for Excel — includes financial columns for reconciliation
      const excelData = transactionsToExport.map((transaction) => {
        const row = {};

        // Add Type column for All tab
        if (selectedTab === "All") {
          row[t("transactions.type")] =
            transaction._transactionType === 'deposit'
              ? t("transactions.depositType")
              : transaction._transactionType === 'payout'
                ? t("transactions.payoutType")
                : transaction._transactionType === 'withdrawal'
                  ? t("transactions.withdrawalType")
                  : transaction._transactionType || '--';
        }

        // Core columns (always included)
        row[t("transactions.exportHeaders.transactionId")] = transaction.transactionId || transaction.id || '--';
        row[t("transactions.exportHeaders.invoiceNo")] = transaction.invoiceNo || '--';
        row[t("transactions.exportHeaders.amount")] = transaction.amount;
        row[t("transactions.exportHeaders.fee")] = transaction.fee || 0;
        row[t("transactions.exportHeaders.netAmount")] = transaction.netAmount || 0;
        row[t("transactions.exportHeaders.currency")] = transaction.currency;
        row[t("transactions.exportHeaders.paymentStatus")] = transaction.status;

        // Wallet column — tab-aware header, safe fallback
        const walletHeader = selectedTab === 'Deposit'
          ? t("transactions.receivingWallet")
          : selectedTab === 'Payout' || selectedTab === 'Withdrawal'
            ? t("transactions.destinationWallet")
            : t("transactions.wallet");
        const walletValue = transaction.receivingWallet && transaction.receivingWallet !== "N/A"
          ? transaction.receivingWallet
          : transaction.clientWallet || '--';
        row[walletHeader] = walletValue;

        row[t("transactions.exportHeaders.date")] = transaction.date;

        return row;
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths to match actual exported columns
      const colWidths = selectedTab === "All"
        ? [
            { wch: 12 }, // Type
            { wch: 25 }, // Transaction ID
            { wch: 18 }, // Invoice No
            { wch: 12 }, // Amount
            { wch: 10 }, // Fee
            { wch: 12 }, // Net Amount
            { wch: 15 }, // Currency
            { wch: 14 }, // Status
            { wch: 42 }, // Wallet
            { wch: 22 }, // Date
          ]
        : [
            { wch: 25 }, // Transaction ID
            { wch: 18 }, // Invoice No
            { wch: 12 }, // Amount
            { wch: 10 }, // Fee
            { wch: 12 }, // Net Amount
            { wch: 15 }, // Currency
            { wch: 14 }, // Status
            { wch: 42 }, // Wallet
            { wch: 22 }, // Date
          ];
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");

      // Generate filename with date range if applicable
      let filename = `transactions_${selectedTab.toLowerCase()}`;

      // Add date range to filename
      if (dateFilter.fromDate && dateFilter.toDate) {
        filename += `_${dateFilter.fromDate}_to_${dateFilter.toDate}`;
      } else if (dateFilter.fromDate) {
        filename += `_from_${dateFilter.fromDate}`;
      } else if (dateFilter.toDate) {
        filename += `_until_${dateFilter.toDate}`;
      } else {
        const currentDate = new Date().toISOString().split("T")[0];
        filename += `_${currentDate}`;
      }

      filename += `.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      // Show success message
      const exportType = selectedRows.size > 0 ? "selected" : "filtered";
      // alert(`Successfully exported ${selectedTransactions.length} ${exportType} transaction(s) to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      alert(t("transactions.failedToExport"));
    } finally {
      setExportLoading(false);
    }
  };

  const getPopoverPosition = () => {
    if (amountButtonRef.current) {
      const rect = amountButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      return {
        top: rect.bottom + window.scrollY + 8,
        left: isMobile
          ? Math.max(
              16,
              Math.min(rect.left + window.scrollX, window.innerWidth - 320),
            )
          : rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };

  const getCurrencyPopoverPosition = () => {
    if (currencyButtonRef.current) {
      const rect = currencyButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      return {
        top: rect.bottom + window.scrollY + 8,
        left: isMobile
          ? Math.max(
              16,
              Math.min(rect.left + window.scrollX, window.innerWidth - 200),
            )
          : rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };

  const getStatusPopoverPosition = () => {
    if (statusButtonRef.current) {
      const rect = statusButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      return {
        top: rect.bottom + window.scrollY + 8,
        left: isMobile
          ? Math.max(
              16,
              Math.min(rect.left + window.scrollX, window.innerWidth - 200),
            )
          : rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };

  const getTypePopoverPosition = () => {
    if (typeButtonRef.current) {
      const rect = typeButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      return {
        top: rect.bottom + window.scrollY + 8,
        left: isMobile
          ? Math.max(
              16,
              Math.min(rect.left + window.scrollX, window.innerWidth - 200),
            )
          : rect.left + window.scrollX,
      };
    }
    return { top: 0, left: 0 };
  };

  const getDatePopoverPosition = () => {
    if (dateButtonRef.current) {
      const rect = dateButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;
      const popoverWidth = isMobile ? 300 : 320;

      // Calculate the left position to center the popover under the Date column
      let leftPosition = rect.left + window.scrollX;

      // On mobile, ensure it doesn't go off screen
      if (isMobile) {
        leftPosition = Math.max(
          16,
          Math.min(leftPosition, window.innerWidth - popoverWidth),
        );
      } else {
        // On desktop, center it under the Date column
        leftPosition =
          rect.left + window.scrollX - (popoverWidth - rect.width) / 2;
        // Ensure it doesn't go off screen
        leftPosition = Math.max(
          16,
          Math.min(leftPosition, window.innerWidth - popoverWidth),
        );
      }

      return {
        top: rect.bottom + window.scrollY + 8,
        left: leftPosition,
      };
    }
    return { top: 0, left: 0 };
  };

  // getEditColumnsPopoverPosition removed — column customization removed

  return (
    <div className="space-y-4 lg:space-y-6 pt-5 sm:pt-8 md:pt-3">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
          {t("transactions.title")}
        </h1>

        {/* Action Buttons - Responsive Layout */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          {/* Create Payment Link — only on All and Deposit tabs */}
          {(selectedTab === "All" || selectedTab === "Deposit") && (
            <Permission permission="qr-code-generation">
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  background:
                    "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                  borderRadius: "10px",
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors w-full sm:w-auto flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>{t("transactions.createPaymentLink")}</span>
              </button>
            </Permission>
          )}
        </div>
      </div>

      {/* Transaction Tabs and Filters */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        {/* Filters Section - Top on mobile, right on desktop */}
        <div className="flex flex-row items-center gap-2 md:order-2">
          {/* Reset Filters Button - Can wrap if needed */}
          {hasActiveFilters() && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 border border-red-300 rounded-lg font-medium text-red-700 hover:bg-red-50 transition-colors text-xs sm:text-sm whitespace-nowrap"
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span className="hidden sm:inline">
                {t("transactions.reset")}
              </span>
            </button>
          )}

          {/* Date Range Picker, Export, and Columns - Always in one row */}
          <div className="flex flex-row items-center gap-2 flex-nowrap">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("common.search") + "..."}
                className="pl-8 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white placeholder-gray-400 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors w-44 sm:w-56"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="relative date-picker">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-2 sm:px-3 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors text-xs sm:text-sm whitespace-nowrap"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="whitespace-nowrap hidden sm:inline">
                  {new Date(fromDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}{" "}
                  -{" "}
                  {new Date(toDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span className="whitespace-nowrap sm:hidden">
                  {new Date(fromDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </button>

              {showDatePicker && (
                <div
                  className="
      absolute 
      mt-2 
      w-72 sm:w-80 lg:w-80 
      bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 
      left-1/2 -translate-x-1/3 sm:left-auto sm:translate-x-0 sm:right-0
    "
                >
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {t("transactions.selectDateRange")}
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t("transactions.fromDate")}
                        </label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t("transactions.toDate")}
                        </label>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="w-full sm:w-auto px-3 py-2 text-xs text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg"
                      >
                        {t("transactions.cancel")}
                      </button>
                      <button
                        onClick={() => {
                          if (fromDate && toDate) {
                            // Update the dateFilter state which will trigger the useEffect to fetch transactions
                            setDateFilter({
                              fromDate: fromDate,
                              toDate: toDate,
                              preset: "custom",
                            });
                          }
                          setShowDatePicker(false);
                        }}
                        disabled={!fromDate || !toDate}
                        className="w-full sm:w-auto px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t("transactions.apply")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Export Dropdown Button */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={formattedTransactions.length === 0 || exportLoading}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 border border-gray-300 rounded-lg font-medium transition-colors text-xs sm:text-sm whitespace-nowrap ${
                  formattedTransactions.length === 0 || exportLoading
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {exportLoading ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 flex-shrink-0" />
                )}
                <span className="hidden sm:inline">
                  {exportLoading ? t("transactions.exporting") : t("transactions.export")}
                </span>
                {!exportLoading && <ChevronDown className="w-3 h-3 flex-shrink-0" />}
              </button>

              {showExportDropdown && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                  <button
                    onClick={() => handleExport('all')}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-gray-400" />
                    <div className="text-left">
                      <div className="font-medium">{t("transactions.exportAll")}</div>
                      <div className="text-xs text-gray-400">{t("transactions.exportAllDesc")}</div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleExport('page')}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-gray-400" />
                    <div className="text-left">
                      <div className="font-medium">{t("transactions.exportCurrentPage")}</div>
                      <div className="text-xs text-gray-400">{pagination.limit} {t("transactions.exportRowsLabel")}</div>
                    </div>
                  </button>
                  {selectedRows.size > 0 && (
                    <button
                      onClick={() => handleExport('selected')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <CheckSquare className="w-4 h-4 text-blue-500" />
                      <div className="text-left">
                        <div className="font-medium">{t("transactions.exportSelectedOnly")}</div>
                        <div className="text-xs text-gray-400">{selectedRows.size} {t("transactions.exportRowsLabel")}</div>
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Edit Columns Button */}
            {/* Column customization button removed — columns are fixed per tab */}
          </div>
        </div>

        {/* Tabs Section - Bottom on mobile, left on desktop */}
        <div className="flex space-x-4 sm:space-x-8 overflow-x-auto border-b border-gray-200 md:w-4/5 md:order-1">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setSelectedTab(tab.value)}
              className={`pb-3 font-medium text-sm transition-colors whitespace-nowrap ${
                selectedTab === tab.value
                  ? "text-[#348BED] border-b-2 border-[#348BED]"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Selection Counter */}
      {selectedRows.size > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                {t("transactions.selected", {
                  count: selectedRows.size,
                  plural: selectedRows.size !== 1 ? "s" : "",
                })}
              </span>
            </div>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              {t("transactions.clearSelection")}
            </button>
          </div>
        </div>
      )}

      {/* Transaction Table */}
      <div className="bg-white rounded-[25px] p-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] ">
            <thead className="bg-white border-b border-gray-200">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === transactions.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                {visibleColumns.amount && (
                  <th
                    className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 capitalize tracking-wider"
                    style={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    <div className="relative">
                      <button
                        ref={amountButtonRef}
                        onClick={handleAmountFilterClick}
                        className={`flex items-center gap-1 hover:text-gray-700 transition-colors ${
                          amountFilter.min > 0 || amountFilter.max < 100000
                            ? "text-blue-600 font-semibold"
                            : ""
                        }`}
                      >
                        {t("transactions.amount")}
                        {/* {(amountFilter.min > 0 || amountFilter.max < 100000) && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )} */}
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Amount Range Popover */}
                      <AmountRangePopover
                        isOpen={amountPopoverOpen}
                        onClose={() => setAmountPopoverOpen(false)}
                        onApply={handleAmountFilterApply}
                        position={getPopoverPosition()}
                      />
                    </div>
                  </th>
                )}
                {visibleColumns.currency && (
                  <th className="px-3 sm:px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    <div className="relative">
                      <button
                        ref={currencyButtonRef}
                        onClick={handleCurrencyFilterClick}
                        className={`flex items-center gap-1 hover:text-gray-700 transition-colors ${
                          selectedCurrency.id !== "all"
                            ? "text-blue-600 font-semibold"
                            : ""
                        }`}
                      >
                        {t("transactions.currency")}
                        {/* {selectedCurrency.id !== 'usdt-trc20' && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )} */}
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Currency Selection Popover */}
                      <CurrencyPopover
                        isOpen={currencyPopoverOpen}
                        onClose={() => setCurrencyPopoverOpen(false)}
                        onSelect={handleCurrencySelect}
                        position={getCurrencyPopoverPosition()}
                        selectedCurrency={selectedCurrency}
                      />
                    </div>
                  </th>
                )}
                {visibleColumns.status && (
                  <th className="px-3 sm:px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    <div className="relative">
                      <button
                        ref={statusButtonRef}
                        onClick={handleStatusFilterClick}
                        className={`flex items-center gap-1 hover:text-gray-700 transition-colors ${
                          selectedStatus.id && selectedStatus.id !== ""
                            ? "text-blue-600 font-semibold"
                            : ""
                        }`}
                      >
                        {t("transactions.status")}
                        {/* {selectedStatus.id && selectedStatus.id !== '' && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )} */}
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Status Selection Popover */}
                      <StatusPopover
                        isOpen={statusPopoverOpen}
                        onClose={() => setStatusPopoverOpen(false)}
                        onSelect={handleStatusSelect}
                        position={getStatusPopoverPosition()}
                        selectedStatus={selectedStatus}
                        selectedTab={selectedTab}
                      />
                    </div>
                  </th>
                )}
                {visibleColumns.type && (
                  <th
                    className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 capitalize tracking-wider"
                    style={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    {t("transactions.type")}
                  </th>
                )}
                {visibleColumns.receivingWallet && (
                  <th
                    className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 capitalize tracking-wider hidden lg:table-cell"
                    style={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    {selectedTab === 'Deposit'
                      ? t("transactions.receivingWallet")
                      : selectedTab === 'Payout' || selectedTab === 'Withdrawal'
                        ? t("transactions.destinationWallet")
                        : t("transactions.wallet")}
                  </th>
                )}
                {visibleColumns.date && (
                  <th
                    className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 capitalize tracking-wider"
                    style={{ fontSize: "14px", textTransform: "capitalize" }}
                  >
                    <div className="relative">
                      <button
                        ref={dateButtonRef}
                        onClick={handleDateFilterClick}
                        className={`flex items-center gap-1 hover:text-gray-700 transition-colors ${
                          dateFilter.fromDate !== "" || dateFilter.toDate !== ""
                            ? "text-blue-600 font-semibold"
                            : ""
                        }`}
                      >
                        {t("transactions.date")}
                        {(dateFilter.fromDate !== "" ||
                          dateFilter.toDate !== "") && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Date Range Popover */}
                      <DatePopover
                        isOpen={datePopoverOpen}
                        onClose={() => setDatePopoverOpen(false)}
                        onApply={handleDateFilterApply}
                        position={getDatePopoverPosition()}
                      />
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <>
                  {[...Array(6)].map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                      <td className="px-3 sm:px-6 py-3">
                        <span className="inline-block w-4 h-4 bg-gray-200 rounded"></span>
                      </td>
                      {visibleColumns.amount && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                          <span className="inline-block h-4 bg-gray-200 rounded w-20"></span>
                        </td>
                      )}
                      {visibleColumns.currency && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                          <span className="inline-block h-4 bg-gray-200 rounded w-24"></span>
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                          <span className="inline-block h-6 bg-gray-200 rounded-[8px] w-20"></span>
                        </td>
                      )}
                      {visibleColumns.type && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                          <span className="inline-block h-6 bg-gray-200 rounded-full w-20"></span>
                        </td>
                      )}
                      {visibleColumns.receivingWallet && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap hidden lg:table-cell">
                          <span className="inline-block h-4 bg-gray-200 rounded w-40"></span>
                        </td>
                      )}
                      {visibleColumns.date && (
                        <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                          <span className="inline-block h-4 bg-gray-200 rounded w-28"></span>
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td
                    colSpan={selectedTab === "All" ? 7 : 6}
                    className="px-6 py-8 text-center"
                  >
                    <div className="text-red-600">
                      <svg
                        className="w-12 h-12 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <p className="text-lg font-medium">
                        {t("transactions.errorLoading")}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{error}</p>
                      <button
                        onClick={() => {
                          const tabTypeMap = {
                            All: "all",
                            Deposit: "deposit",
                            Payout: "payout",
                            Withdrawal: "withdrawal",
                          };
                          const apiType = tabTypeMap[selectedTab] || "all";
                          fetchTransactions(apiType);
                        }}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {t("transactions.tryAgain")}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : formattedTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan={selectedTab === "All" ? 7 : 6}
                    className="px-6 py-8 text-center"
                  >
                    <div className="text-gray-500">
                      <svg
                        className="w-12 h-12 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-lg font-medium">
                        {t("transactions.noTransactionsFound")}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {t("transactions.noTransactionsMessage")}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                formattedTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openTransactionModal(transaction)}
                  >
                    <td
                      className="px-3 sm:px-6 py-3 whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.has(transaction.id)}
                        onChange={() => handleSelectRow(transaction.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    {visibleColumns.amount && (
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-500">
                        {transaction.actualBalance || transaction.amount}
                      </td>
                    )}
                    {visibleColumns.currency && (
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        <span className="hidden sm:inline">
                          {transaction.currency}
                        </span>
                        <span className="sm:hidden">
                          {transaction.currency.split(" ")[0]}
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-[8px] border text-xs font-medium ${
                            transaction.status === "initiated"
                              ? "bg-blue-100 text-blue-800 border-blue-300"
                              : transaction.status === "confirmed"
                                ? "bg-green-100 text-green-800 border-green-300"
                                : transaction.status === "completed"
                                  ? "bg-green-100 text-green-800 border-green-300"
                                  : transaction.status === "rejected"
                                    ? "bg-red-100 text-red-800 border-red-300"
                                    : transaction.status === "failed"
                                      ? "bg-red-100 text-red-800 border-red-300"
                                      : transaction.status === "expired"
                                        ? "bg-red-100 text-red-800 border-red-300"
                                        : transaction.status === "pending"
                                          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                          : transaction.status === "api_failed"
                                            ? "bg-orange-100 text-orange-800 border-orange-300"
                                            : "bg-gray-100 text-gray-800 border-gray-300"
                          }`}
                        >
                          {/* Status Icons */}
                          {transaction.status === "confirmed" ||
                          transaction.status === "completed" ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : transaction.status === "rejected" ||
                            transaction.status === "failed" ||
                            transaction.status === "expired" ? (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          ) : transaction.status === "pending" ? (
                            <Clock className="w-3 h-3 mr-1" />
                          ) : transaction.status === "initiated" ? (
                            <Send className="w-3 h-3 mr-1" />
                          ) : transaction.status === "api_failed" ? (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          ) : null}

                          <span className="hidden sm:inline capitalize">
                            {transaction.status === "confirmed" &&
                            transaction.transactionType ===
                              "deposit_detected_wallet"
                              ? "confirmed"
                              : transaction.status === "confirmed" ||
                                  transaction.status === "completed"
                                ? "paid"
                                : transaction.status}
                          </span>
                          <span className="sm:hidden">
                            {transaction.status === "confirmed" ||
                            transaction.status === "completed"
                              ? "✓"
                              : transaction.status === "rejected" ||
                                  transaction.status === "failed"
                                ? "✗"
                                : transaction.status === "pending"
                                  ? "⏱"
                                  : transaction.status === "initiated"
                                    ? "→"
                                    : transaction.status[0]?.toUpperCase()}
                          </span>
                        </span>
                      </td>
                    )}
                    {visibleColumns.type && (
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            transaction._transactionType === 'deposit'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : transaction._transactionType === 'payout'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : transaction._transactionType === 'withdrawal'
                                  ? 'bg-orange-50 text-orange-700 border border-orange-200'
                                  : 'bg-gray-50 text-gray-700 border border-gray-200'
                          }`}
                        >
                          {transaction._transactionType === 'deposit'
                            ? t("transactions.depositType")
                            : transaction._transactionType === 'payout'
                              ? t("transactions.payoutType")
                              : transaction._transactionType === 'withdrawal'
                                ? t("transactions.withdrawalType")
                                : transaction._transactionType || '--'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.receivingWallet && (
                      <td className="px-3 sm:px-6 py-3 text-sm text-gray-500 font-mono hidden lg:table-cell">
                        <span className="truncate max-w-[200px] block">
                          {transaction.receivingWallet && transaction.receivingWallet !== "N/A"
                            ? transaction.receivingWallet
                            : transaction.clientWallet || '--'}
                        </span>
                      </td>
                    )}
                    {/* transactionId and invoiceNo columns removed — available in detail modal */}
                    {visibleColumns.date && (
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        <span className="hidden sm:inline">
                          {transaction.date}
                        </span>
                        <span className="sm:hidden">
                          {transaction.date.split(",")[1]}
                        </span>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
      </div>
      <div className="px-3 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
          <span className="hidden sm:inline">
            {t("transactions.viewingResults", {
              from: (pagination.page - 1) * pagination.limit + 1,
              to: Math.min(
                pagination.page * pagination.limit,
                pagination.total,
              ),
              total: pagination.total,
            })}
          </span>
          <span className="sm:hidden">
            {t("transactions.viewingResultsMobile", {
              from: (pagination.page - 1) * pagination.limit + 1,
              to: Math.min(
                pagination.page * pagination.limit,
                pagination.total,
              ),
              total: pagination.total,
            })}
          </span>
        </div>
        <div className="flex items-center gap-2 order-1 sm:order-2">
          <button
            onClick={() => {
              if (pagination.page > 1) {
                const tabTypeMap = {
                  All: "all",
                  Deposit: "deposit",
                  Payout: "payout",
                  Withdrawal: "withdrawal",
                };
                const apiType = tabTypeMap[selectedTab] || "all";
                fetchTransactions(
                  apiType,
                  pagination.page - 1,
                  pagination.limit,
                );
              }
            }}
            disabled={pagination.page <= 1}
            className="px-3 py-1 text-xs sm:text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("transactions.previous")}
          </button>
          <span className="px-3 py-1 text-xs sm:text-sm text-gray-700">
            {t("transactions.page")} {pagination.page} {t("transactions.of")}{" "}
            {pagination.totalPages}
          </span>
          <button
            onClick={() => {
              if (pagination.page < pagination.totalPages) {
                const tabTypeMap = {
                  All: "all",
                  Deposit: "deposit",
                  Payout: "payout",
                  Withdrawal: "withdrawal",
                };
                const apiType = tabTypeMap[selectedTab] || "all";
                fetchTransactions(
                  apiType,
                  pagination.page + 1,
                  pagination.limit,
                );
              }
            }}
            disabled={pagination.page >= pagination.totalPages}
            className="px-3 py-1 text-xs sm:text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("transactions.next")}
          </button>
        </div>
      </div>
      {/* Withdraw Requests Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg  max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                {t("transactions.payoutRequests")}
              </h2>

              <button
                onClick={() => setShowWithdrawModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              {/* <p className="text-gray-600 text-sm md:text-base">
            Submit new Payout requests and track their status
          </p> */}
            </div>

            {/* Modal Content */}
            <div
              className="overflow-y-auto max-h-[calc(90vh-80px)] p-0"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .modal-content::-webkit-scrollbar {
                    display: none;
                  }
                `,
                }}
              />
              <div className="modal-content">
                <NewPayoutRequest setShowWithdrawModal={setShowWithdrawModal} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#F6F7F8] rounded-[19px] max-w-xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 ">
              <div className="flex items-center space-x-3">
                {/* <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div> */}
                <div>
                  <h2 className="text-md sm:text-2xl font-semibold text-gray-900">
                    {t("transactions.transactionDetails")}
                  </h2>
                  <p className="text-sm text-[#667085]">
                    {t("transactions.transactionInfo")}
                  </p>
                </div>
              </div>
              <button
                onClick={closeTransactionModal}
                className="p-2 text-gray-400 bg-[#D1D5DB87] rounded-full hover:text-gray-600 hover:bg-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div
              className="overflow-y-auto max-h-[calc(90vh-120px)] p-4 sm:p-6"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .transaction-modal-content::-webkit-scrollbar {
                    display: none;
                  }
                `,
                }}
              />
              <div className="transaction-modal-content">
                {/* Payment details Section */}
                <div>
                  <h3 className="text-md font-medium text-[#1A1C1E] mb-3">
                    {t("transactions.paymentDetails")}
                  </h3>
                  <div className="space-y-4 bg-white p-4 rounded-[15px]">
                    {/* Payment Status */}
                    <div className="flex items-center justify-between  ">
                      <span className="text-sm font-small text-[#667085]">
                        {t("transactions.payment")}
                      </span>
                      <span
                        className={`text-sm font-medium capitalize ${
                          {
                            initiated: "text-blue-500",
                            confirmed: "text-green-500",
                            completed: "text-green-600",
                            rejected: "text-red-600",
                            failed: "text-red-600",
                            expired: "text-red-500",
                            pending: "text-yellow-500",
                            api_failed: "text-orange-500",
                          }[selectedTransaction.status?.toLowerCase()] ||
                          "text-gray-600"
                        }`}
                      >
                        {selectedTransaction.status === "confirmed" &&
                        selectedTransaction.transactionType ===
                          "deposit_detected_wallet"
                          ? "confirmed"
                          : selectedTransaction.status === "confirmed" ||
                              selectedTransaction.status === "completed"
                            ? "paid"
                            : selectedTransaction.status || "N/A"}
                      </span>
                    </div>

                    {/* Transaction ID */}
                    <div className="flex items-center justify-between ">
                      <span className="text-sm font-medium text-[#667085]">
                        {t("transactions.transactionIdLabel")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-mono text-gray-900 whitespace-nowrap"
                          title={
                            selectedTransaction.transactionId ||
                            selectedTransaction.id
                          } // full ID on hover
                        >
                          {selectedTransaction.transactionId ||
                          selectedTransaction.id
                            ? `${(selectedTransaction.transactionId || selectedTransaction.id).slice(0, 6)}....${(selectedTransaction.transactionId || selectedTransaction.id).slice(-6)}`
                            : "N/A"}
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedTransaction.transactionId ||
                                selectedTransaction.id,
                            )
                          }
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={t("transactions.copy")}
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>

                    {/* Actual amount received */}
                    <div className="flex items-center justify-between ">
                      <span className="text-sm font-small text-[#667085]">
                        {t("transactions.actualAmountReceived")}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedTransaction.actualBalance || "0"} USDT
                      </span>
                    </div>
                    <div className="flex items-center justify-between ">
                      <span className="text-sm font-small text-[#667085]">
                        {t("transactions.requestAmount")}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedTransaction.amount || "0"} USDT
                      </span>
                    </div>

                    {/* Fee */}
                    {selectedTransaction.fee && (
                      <div className="flex items-center justify-between ">
                        <span className="text-sm font-small text-[#667085]">
                          {t("transactions.fee")}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedTransaction.fee} USDT
                        </span>
                      </div>
                    )}

                    {/* Currency Type */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#667085]">
                        {t("transactions.currencyType")}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                          <img
                            src={selectedTransaction.currency?.toUpperCase().includes("ERC") ? "/ERC.png" : "/TRC.png"}
                            alt="USDT"
                            className="w-6 h-6 object-contain"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedTransaction.currency || "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Wallet Address */}
                    <div className="flex items-center justify-between ">
                      <span className="text-sm font-small text-[#667085]">
                        {t("transactions.walletAddress")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-mono text-gray-900 whitespace-nowrap"
                          title={
                            (selectedTransaction.receivingWallet !== "N/A"
                              ? selectedTransaction.receivingWallet
                              : selectedTransaction.clientWallet) || "N/A"
                          }
                        >
                          {(() => {
                            const wallet =
                              selectedTransaction.receivingWallet !== "N/A"
                                ? selectedTransaction.receivingWallet
                                : selectedTransaction.clientWallet;

                            return wallet &&
                              wallet !== "N/A" &&
                              wallet.length > 12
                              ? `${wallet.slice(0, 6)}....${wallet.slice(-6)}`
                              : wallet || "N/A";
                          })()}
                        </span>

                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedTransaction.receivingWallet !== "N/A"
                                ? selectedTransaction.receivingWallet
                                : selectedTransaction.clientWallet,
                            )
                          }
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={t("transactions.copy")}
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {selectedTab === "Deposit" && (
                    <>
                      <div className="space-y-4 bg-white p-4 mt-3 rounded-[15px]">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#1A1C1E]">
                            {t("transactions.paymentStatus")}
                          </span>

                          {(() => {
                            const amount = selectedTransaction.rawAmount || 0;
                            const actualBalance =
                              selectedTransaction.actualBalance || 0;

                            let paymentStatus = "";
                            let statusColor = "text-green-600";

                            if (
                              selectedTab === "Deposit" &&
                              selectedTransaction.hasActualBalance
                            ) {
                              if (
                                ["confirmed", "completed", "approved"].includes(
                                  selectedTransaction.status,
                                )
                              ) {
                                if (actualBalance < amount) {
                                  paymentStatus = t("transactions.underpaid");
                                  statusColor = "text-red-600";
                                } else if (actualBalance > amount) {
                                  paymentStatus = t("transactions.overpaid");
                                  statusColor = "text-yellow-600";
                                } else {
                                  paymentStatus = t("transactions.exactPaid");
                                  statusColor = "text-green-600";
                                }
                              } else {
                                paymentStatus =
                                  selectedTransaction.status || "Pending";
                                statusColor = "text-gray-600";
                              }
                            } else {
                              paymentStatus =
                                selectedTransaction.status || "Pending";
                              statusColor = "text-gray-600";
                            }

                            // choose icon
                            let IconComponent = null;
                            switch (selectedTransaction.status) {
                              case "confirmed":
                              case "completed":
                                IconComponent = CheckCircle;
                                break;
                              case "rejected":
                              case "failed":
                              case "expired":
                              case "api_failed":
                                IconComponent = AlertCircle;
                                break;
                              case "pending":
                                IconComponent = Clock;
                                break;
                              case "initiated":
                                IconComponent = Send;
                                break;
                              default:
                                IconComponent = null;
                            }

                            return (
                              <div
                                className={`flex items-center gap-1 text-sm font-medium ${statusColor}`}
                              >
                                {IconComponent && (
                                  <IconComponent className="w-4 h-4" />
                                )}
                                <span className="capitalize">
                                  {paymentStatus}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  )}

                  {/* User Details Section - Only show for deposit_detected_wallet with userDetails */}
                  {selectedTransaction.transactionType ===
                    "deposit_detected_wallet" &&
                    selectedTransaction.userDetails && (
                      <div className="space-y-4 bg-white p-4 mt-3 rounded-[15px]">
                        <h4 className="text-sm font-medium text-[#1A1C1E] mb-3">
                          {t("transactions.userDetails")}
                        </h4>

                        {selectedTransaction.userDetails.userId && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-small text-[#667085]">
                              {t("transactions.userId")}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedTransaction.userDetails.userId}
                            </span>
                          </div>
                        )}

                        {selectedTransaction.userDetails.email && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-small text-[#667085]">
                              {t("transactions.email")}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedTransaction.userDetails.email}
                            </span>
                          </div>
                        )}

                        {selectedTransaction.userDetails.phone && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-small text-[#667085]">
                              {t("transactions.phone")}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedTransaction.userDetails.phone}
                            </span>
                          </div>
                        )}

                        {(selectedTransaction.userDetails.firstName ||
                          selectedTransaction.userDetails.lastName) && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-small text-[#667085]">
                              {t("transactions.name")}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {[
                                selectedTransaction.userDetails.firstName,
                                selectedTransaction.userDetails.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ") || "N/A"}
                            </span>
                          </div>
                        )}

                        {selectedTransaction.userDetails.description && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-small text-[#667085]">
                              {t("transactions.description")}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedTransaction.userDetails.description}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                </div>

                {/* User Details Section - Only show if any user detail exists */}
                {(selectedTransaction.username ||
                  selectedTransaction.firstName ||
                  selectedTransaction.lastName ||
                  selectedTransaction.email ||
                  selectedTransaction.phoneNumber ||
                  selectedTransaction.description ||
                  selectedTransaction.referenceId) && (
                  <div className="mt-3">
                    <h3 className="text-md font-medium text-[#1A1C1E] mb-3">
                      {t("transactions.userDetails")}
                    </h3>
                    <div className="space-y-4 bg-white p-4 rounded-[15px]">
                      {/* Username */}
                      {selectedTransaction.username && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.username")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.username}
                          </span>
                        </div>
                      )}

                      {/* First Name */}
                      {selectedTransaction.firstName && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.firstName")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.firstName}
                          </span>
                        </div>
                      )}

                      {/* Last Name */}
                      {selectedTransaction.lastName && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.lastName")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.lastName}
                          </span>
                        </div>
                      )}

                      {/* Email */}
                      {selectedTransaction.email && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.email")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.email}
                          </span>
                        </div>
                      )}

                      {/* Phone Number */}
                      {selectedTransaction.phoneNumber && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.phoneNumber")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.phoneNumber}
                          </span>
                        </div>
                      )}

                      {/* Description */}
                      {selectedTransaction.description && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.description")}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedTransaction.description}
                          </span>
                        </div>
                      )}

                      {/* Reference ID */}
                      {selectedTransaction.referenceId && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-small text-[#667085]">
                            {t("transactions.referenceId")}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {selectedTransaction.referenceId}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(selectedTransaction.referenceId)
                              }
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              title={t("transactions.copy")}
                            >
                              <Copy className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedTransaction.status !== "expired" && (
                  <div className="bg-white p-4 rounded-[15px] mt-3">
                    <div className={`grid grid-cols-1 ${selectedTransaction.confirmedAt ? 'md:grid-cols-2' : ''} gap-6`}>
                      {/* Created At */}
                      <div className={selectedTransaction.confirmedAt ? "pr-6 border-r border-gray-200" : ""}>
                        <div className="text-sm font-medium text-gray-600 mb-2">
                          {t("transactions.createdAt")}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <FaCalendarAlt className="w-4 h-4 text-[#667085BF]" />
                          <span>{formatDate(selectedTransaction.createdAt)}</span>
                          <GoClockFill className="w-4 h-4 text-[#667085BF]" />
                          <span>{formatTime(selectedTransaction.createdAt)}</span>
                        </div>
                      </div>

                      {/* Status Updated At — only show when there's a date */}
                      {selectedTransaction.confirmedAt && (
                        <div className="pl-6">
                          <div className="text-sm font-medium text-gray-600 mb-2">
                            {t("transactions.statusUpdatedAt")}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <FaCalendarAlt className="w-4 h-4 text-[#667085BF]" />
                            <span>{formatDate(selectedTransaction.confirmedAt)}</span>
                            <GoClockFill className="w-4 h-4 text-[#667085BF]" />
                            <span>{formatTime(selectedTransaction.confirmedAt)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* TXN Hash + Invoice ID card — only render when at least one has data */}
                {(selectedTransaction.transactionHash ||
                  selectedTransaction.blockchainTxHash ||
                  selectedTransaction.adminTransferData?.transactionHash ||
                  selectedTransaction.type ||
                  selectedTransaction.invoiceNo) && (
                  <div className="bg-white p-4 rounded-[15px] mt-3 space-y-4">
                    {/* TXN Hash — hidden until hash exists */}
                    {(selectedTransaction.transactionHash ||
                      selectedTransaction.blockchainTxHash ||
                      selectedTransaction.adminTransferData?.transactionHash) && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-small text-[#667085]">
                          {t("transactions.txnHash")}
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-mono text-gray-900 whitespace-nowrap"
                            title={
                              selectedTransaction.transactionHash ||
                              selectedTransaction.blockchainTxHash ||
                              selectedTransaction.adminTransferData
                                ?.transactionHash ||
                              "N/A"
                            }
                          >
                            {(() => {
                              const hash =
                                selectedTransaction.transactionHash ||
                                selectedTransaction.blockchainTxHash ||
                                selectedTransaction.adminTransferData
                                  ?.transactionHash;

                              return hash && hash.length > 12
                                ? `${hash.slice(0, 6)}....${hash.slice(-6)}`
                                : hash || "N/A";
                            })()}
                          </span>

                          {(() => {
                            const hash =
                              selectedTransaction.transactionHash ||
                              selectedTransaction.blockchainTxHash ||
                              selectedTransaction.adminTransferData
                                ?.transactionHash;

                            if (!hash) return null;

                            let explorerUrl = "#";
                            const currency =
                              selectedTransaction.currency?.toUpperCase();

                            if (currency === "USDT-TRC20") {
                              explorerUrl = `https://tronscan.org/#/transaction/${hash}`;
                            } else if (currency === "USDT-ERC20") {
                              explorerUrl = `https://etherscan.io/tx/${hash}`;
                            }

                            return (
                              <button
                                onClick={() =>
                                  window.open(
                                    explorerUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title={t("transactions.viewOnExplorer")}
                              >
                                <LuSquareArrowOutUpRight className="w-4 h-4 text-blue-500" />
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Invoice ID */}
                    {(selectedTransaction.type ||
                      selectedTransaction.invoiceNo) && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-small text-[#667085]">
                          {t("transactions.invoiceId")}
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-mono text-gray-900 whitespace-nowrap"
                            title={
                              selectedTransaction.type ||
                              selectedTransaction.invoiceNo ||
                              "N/A"
                            }
                          >
                            {(() => {
                              const invoice =
                                selectedTransaction.type ||
                                selectedTransaction.invoiceNo;
                              return invoice &&
                                invoice !== "N/A" &&
                                invoice.length > 12
                                ? `${invoice.slice(0, 6)}....${invoice.slice(-6)}`
                                : invoice || "N/A";
                            })()}
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                selectedTransaction.type ||
                                  selectedTransaction.invoiceNo,
                              )
                            }
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title={t("transactions.copy")}
                          >
                            <Copy className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* View Public Receipt */}
                {selectedTransaction.publicToken && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        const baseUrl = `${API_BASE_URL}`.replace(/\/api\/v1$/, '');
                        window.open(`${baseUrl}/tx/${selectedTransaction.publicToken}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-[#1A1C1E] text-white text-sm font-medium rounded-[12px] hover:bg-[#2D2F31] transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Public Receipt
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Payment Link Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleCloseCreateModal}>
          <div className="bg-[#F6F7F8] rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {createdPaymentLink ? t('paymentLinks.paymentLinkCreated') : t('paymentLinks.createPaymentLink')}
                </h2>
                {!createdPaymentLink && (
                  <p className="text-sm text-gray-500 mt-0.5">{t('paymentLinks.createForm.subtitle')}</p>
                )}
              </div>
              <button onClick={handleCloseCreateModal} className="p-1.5 hover:bg-gray-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {createdPaymentLink ? (
              /* ===== SUCCESS VIEW ===== */
              <div className="p-6 pt-2 space-y-4">
                <div className="flex flex-col items-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{t('paymentLinks.paymentLinkCreated')}</p>
                </div>
                {/* Payment URL */}
                <div className="bg-white rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.paymentUrl')}</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-blue-600 break-all">{createdPaymentLink.url}</span>
                    <button onClick={() => copyWithFeedback(createdPaymentLink.url, 'paymentUrl')} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0">
                      {copiedField === 'paymentUrl' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {/* Transaction ID */}
                {createdPaymentLink.transactionId && (
                  <div className="bg-white rounded-xl p-4">
                    <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.transactionId')}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 break-all">{createdPaymentLink.transactionId}</span>
                      <button onClick={() => copyWithFeedback(createdPaymentLink.transactionId, 'createdTxId')} className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0">
                        {copiedField === 'createdTxId' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                {/* Invoice No */}
                {createdPaymentLink.invoiceNo && (
                  <div className="bg-white rounded-xl p-4">
                    <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.invoiceId')}</div>
                    <span className="text-sm font-medium text-gray-900">{createdPaymentLink.invoiceNo}</span>
                  </div>
                )}
                {/* Action Buttons */}
                <div className="space-y-3 pt-2">
                  <button
                    onClick={() => window.open(createdPaymentLink.url, '_blank')}
                    style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)", borderRadius: "10px" }}
                    className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium hover:opacity-90 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('paymentLinks.openPaymentPage')}
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setCreatedPaymentLink(null); setCreateForm({ amount: '', currencyType: 'USDT-TRC20', timer: '', description: '' }); setCreateError(''); }}
                      className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-[10px] font-medium hover:bg-gray-50 transition-colors text-sm"
                    >
                      {t('paymentLinks.createAnother')}
                    </button>
                    <button onClick={handleCloseCreateModal} className="flex-1 py-2.5 bg-gray-900 text-white rounded-[10px] font-medium hover:bg-gray-800 transition-colors text-sm">
                      {t('paymentLinks.done')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ===== FORM VIEW ===== */
              <div className="px-6 pb-6 pt-2 space-y-5">
                {createError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-700">{createError}</span>
                  </div>
                )}
                {/* Row 1: Amount + Currency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.amount')}</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                      <input type="number" min="0" step="any" value={createForm.amount} onChange={(e) => handleCreateFormChange('amount', e.target.value)} placeholder={t('paymentLinks.createForm.amountPlaceholder')} className="w-full pl-7 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.currency')}</label>
                    <select value={createForm.currencyType} onChange={(e) => handleCreateFormChange('currencyType', e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm appearance-none cursor-pointer" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}>
                      <option value="USDT-TRC20">USDT TRC20</option>
                      <option value="USDT-ERC20">USDT ERC20</option>
                    </select>
                  </div>
                </div>
                {/* Row 2: Timer + Description */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.timer')}</label>
                    <input type="number" min="1" max="2880" value={createForm.timer} onChange={(e) => handleCreateFormChange('timer', e.target.value)} placeholder="30" className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.description')}</label>
                    <input type="text" value={createForm.description} onChange={(e) => handleCreateFormChange('description', e.target.value)} placeholder={t('paymentLinks.createForm.descriptionPlaceholder')} className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm" />
                  </div>
                </div>
                {/* Submit Button */}
                <button onClick={handleCreatePaymentLink} disabled={createLoading} style={{ background: createLoading ? '#9CA3AF' : "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)", borderRadius: "10px" }} className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium hover:opacity-90 transition-colors disabled:cursor-not-allowed mt-2">
                  {createLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />{t('paymentLinks.createForm.creating')}</>) : (<><LinkIcon className="w-4 h-4" />{t('paymentLinks.createPaymentLink')}</>)}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewTransaction;
